// 자료실 — 이 Space 가 **다시 찾을 수 있는 원문의 위치**를 모아 둔 자리.
//
// 2026-09-12 검토가 짚은 것 넷을 여기서 고친다.
//
//   1. 찾을 방법이 스크롤뿐이었다 → 검색과 필터. 필터 선택지는 **실제로 있는 값**에서만
//      만든다(`resource-directory.js`). 「이미지(0)」 같은 칸을 그려 두지 않는다.
//   2. 고른 자료의 정보가 긴 목록 **아래**에 붙었다 → 넓은 화면에서는 목록 옆, 좁은 화면
//      에서는 상세만. 좁은 화면의 「목록으로」는 **검색어와 필터를 그대로** 들고 돌아온다.
//   3. 모든 행이 「검증된 위치」라고 말했다 → 그 말은 하지 않는다. Resource Cloud 가
//      확인한 것은 등록된 형식과 그 판의 불변성이지, 그 주소가 지금 열린다는 사실이 아니다.
//   4. 보관된 Space 의 빈 화면이 「첫 자료 연결」을 권했다 → 쓸 수 없는 자리에는
//      쓰는 버튼을 그리지 않는다.
//
// 그리고 여기 **없는 것**: 미리보기를 만들려고 외부 주소를 열어 보는 코드. 그건 사람이
// 시키지 않은 요청이고, 이 Space 밖으로 나간다. 보여 주는 것은 등록된 사실뿐이다.
import { useCallback, useEffect, useRef, useState } from 'preact/hooks';
import { Link, useLocation, useSearch } from 'wouter-preact';
import { fetchResourceContent, read, registerResource } from '../api/client.js';
import { useCommand, useResource } from '../api/resource.js';
import { describeError } from '../api/errors.js';
import { useSession } from '../session/session.jsx';
import {
  Button, Chip, Dialog, Field, Form, Icon, Input, Notice, PageHead, Panel, Select,
} from '../ui/primitives.jsx';
import { Resource } from '../ui/states.jsx';
import {
  assetSourceSummary, assetVersionLabel, facetOptions, fileSizeLabel, filterAssets, filtersActive,
  findAsset, hasExternalOriginal, isPinnedToOlderVersion, kindLabel, mediaTypeLabel,
  readDirectoryParams, shownVersion, sourceTypeLabel, sourceTypeOf, writeDirectoryParams,
  directoryState,
} from './resource-directory.js';
import { emptyUploadState } from '../api/upload.js';
import { ResourceUploader, UploadUnavailableNotice, useUploadLimits } from './ResourceUploader.jsx';

const KINDS = [
  ['text', '문서'],
  ['image', '이미지'],
  ['url', 'URL'],
  ['video', '동영상'],
];

const EMPTY_REGISTRATION = Object.freeze({
  // 파일을 올릴 것인가, 위치를 연결할 것인가. **사람이 고른다** — 파일을 URL 로 바꿔
  // 오라고 요구하지 않고, 반대로 링크를 파일로 만들라고도 하지 않는다.
  mode: 'file',
  /** 고른 파일의 **핸들**이다(브라우저가 들고 있는 참조). 바이트 사본이 아니고,
   *  어떤 저장소에도 쓰지 않는다 — 이 탭의 메모리에만 있다. */
  file: null,
  upload: null,
  sourceType: 'uri',
  kind: 'url',
  title: '',
  sourceUri: '',
  revision: '',
  commit: '',
  path: '',
  startLine: '',
  endLine: '',
});

export function ResourcesPane({ spaceId, archived }) {
  const session = useSession();
  const search = useSearch();
  const [, navigate] = useLocation();
  const params = readDirectoryParams(search);

  // 치는 동안은 **주소줄을 건드리지 않는다.** 한 글자마다 `replaceState` 를 부르면
  // Safari 는 30초 100회 상한에서 던지고, 라우터를 왕복하는 controlled 입력은 한글
  // 조합 중에 값이 되돌아온다. 그래서 글자는 여기 로컬 state 에 있고, 주소줄은 잠시 뒤
  // 따라온다 — 딥링크·새로고침·공유는 그대로 되고, 타이핑은 라우터를 지나지 않는다.
  const [queryInput, setQueryInput] = useState(params.query);
  const pushedQuery = useRef(params.query);
  const pendingWrite = useRef(null);

  // **지연 쓰기가 보는 것은 언제나 「지금」이다.**
  //
  // 2026-09-12 브라우저 검토에서 이렇게 났다: 글자를 치고 곧바로 자료를 고르면, 뒤늦게
  // 깨어난 타이머가 **자기가 예약될 때의 주소**로 되돌려 고른 자료가 풀렸다. 클로저가
  // 들고 있던 낡은 `params` 가 원인이다. 그래서 최신 값을 ref 로 들고, 쓰는 순간 읽는다.
  const latest = useRef({ params, queryInput });
  latest.current = { params, queryInput };

  const setParams = useCallback((patch, { replace = false } = {}) => {
    // 사람이 무언가를 고르면 아직 안 쓴 검색어도 **함께** 확정된다. 둘을 따로 쓰면
    // 나중에 쓰는 쪽이 먼저 쓴 쪽을 지운다.
    if (pendingWrite.current !== null) {
      clearTimeout(pendingWrite.current);
      pendingWrite.current = null;
    }
    const next = { ...latest.current.params, query: latest.current.queryInput, ...patch };
    pushedQuery.current = next.query;
    navigate(`/s/${spaceId}/resources${writeDirectoryParams(next)}`, { replace });
  }, [navigate, spaceId]);

  useEffect(() => {
    if (queryInput === pushedQuery.current) return undefined;
    pendingWrite.current = setTimeout(() => {
      pendingWrite.current = null;
      setParams({}, { replace: true });
    }, 300);
    return () => {
      if (pendingWrite.current !== null) clearTimeout(pendingWrite.current);
      pendingWrite.current = null;
    };
  }, [queryInput, setParams]);
  // 주소줄이 **밖에서** 바뀐 경우(뒤로가기·링크 열기)에는 칸이 그 값을 따라간다.
  useEffect(() => {
    if (params.query === pushedQuery.current) return;
    pushedQuery.current = params.query;
    setQueryInput(params.query);
  }, [params.query]);

  // `null` 이면 닫힘. `{ assetId }` 가 있으면 **그 자산의 새 판**을 올리는 자리다.
  const [registerTarget, setRegisterTarget] = useState(null);
  // **등록 초안은 대화상자 밖에 산다.** 안에 두면 배경을 잘못 눌러 닫는 순간 적던 것이
  // 사라진다. 닫아도 값은 남고, 다시 열면 그 자리에서 이어 쓴다.
  const [registration, setRegistration] = useState(EMPTY_REGISTRATION);
  const capability = useUploadLimits(spaceId);

  // **다른 Space·다른 Node 는 다른 자리다.** 고른 파일과 진행 중이던 업로드가 그리로
  // 따라가면, 사람은 자기가 어디에 무엇을 올리는지 알 수 없게 된다. 열린 대화상자도 닫는다.
  useEffect(() => {
    setRegistration(EMPTY_REGISTRATION);
    setRegisterTarget(null);
  }, [spaceId, session.nodeId]);

  const resources = useResource(`space:${spaceId}:resources`, () =>
    read('resources', { nodeId: session.nodeId, query: { spaceId } }),
  );
  // Task Ledger의 index다. manifest를 Task 쪽으로 보내지 않고, 검증되어 저장된
  // asset locator와 Task 제목·상태만 읽는다.
  const referenceIndex = useResource(`space:${spaceId}:reference-index`, () =>
    read('reference-index', { nodeId: session.nodeId, query: { spaceId } }),
  );

  return (
    <section class="work resources-page">
      <PageHead
        title="자료"
        meta="이 Space가 함께 쓰는 파일과 원문 위치"
        actions={archived ? null : <Button size="sm" onClick={() => setRegisterTarget({ assetId: null })}>자료 추가</Button>}
      />

      <div class="work__body resources-pane">
        {archived ? (
          <Notice tone="neutral" title="보관된 Space입니다.">자료의 위치는 읽을 수 있지만 새 참조는 등록할 수 없습니다.</Notice>
        ) : null}
        <Resource resource={resources} loadingLabel="자료 목록을 읽는 중">
          {(data) => <ResourceDirectory
            assets={data.assets ?? []}
            params={{ ...params, query: queryInput }}
            setParams={setParams}
            onQuery={setQueryInput}
            spaceId={spaceId}
            archived={archived}
            referenceIndex={referenceIndex}
            capability={capability}
            onAdd={() => setRegisterTarget({ assetId: null })}
            onNewVersion={(asset) => setRegisterTarget({ assetId: asset.assetId, title: asset.title })}
          />}
        </Resource>
      </div>

      {registerTarget ? (
        <Dialog
          title={registerTarget.assetId ? `새 판 올리기 — ${registerTarget.title ?? '이 자료'}` : '자료 추가'}
          description={registerTarget.assetId
            ? '이 자료의 새 판이 됩니다. 이전 판은 그대로 남고, 그 판을 가리키던 참조도 그대로입니다.'
            : '파일을 올리거나, 원문이 있는 위치를 연결합니다.'}
          onClose={() => setRegisterTarget(null)}
        >
          <AddResource
            spaceId={spaceId}
            assetId={registerTarget.assetId ?? null}
            capability={capability}
            draft={registration}
            setDraft={setRegistration}
            onDone={(manifest) => {
              setRegistration(EMPTY_REGISTRATION);
              setRegisterTarget(null);
              // 방금 만들어진 **그 판**을 보여 준다 — 목록 어딘가에 생겼겠거니 하게 두지 않는다.
              // 새 판을 올린 자리에서는 주소에 남아 있던 옛 판을 반드시 지운다. 그러지 않으면
              // 방금 올린 것을 보러 갔는데 이전 판이 열린다.
              if (manifest?.assetId) {
                setParams({ assetId: manifest.assetId, version: manifest.registeredVersion ?? null });
              }
            }}
          />
        </Dialog>
      ) : null}
    </section>
  );
}

/**
 * 자료를 더하는 **두 길**.
 *
 * 파일을 올리거나, 원문이 있는 위치를 연결하거나. 둘은 서로를 대신하지 않는다 —
 * 손에 있는 파일을 어딘가에 올려서 URL 을 만들어 오라고 요구하지 않고, 이미 원본 시스템에
 * 사는 문서를 굳이 복사해 오라고도 하지 않는다.
 *
 * 파일 쪽이 꺼져 있는 배포에서는 **그 이유를 적고** 링크 쪽만 남긴다. 없는 기능을
 * 눌리는 척 그려 두지 않는다.
 */
export function AddResource({ spaceId, assetId, capability, draft, setDraft, onDone }) {
  const canUpload = capability.state === 'ready';
  // **새 판은 앞 판과 같은 종류여야 한다.** 보관 파일 자산에 URL 판을 얹으려 하면 서버가
  // `asset_source_type_immutable` 로 거절한다 — 거절될 길을 화면에 그려 두지 않는다.
  const versioning = Boolean(assetId);
  // 파일 보관이 꺼진 배포에서는 고를 것이 하나뿐이다 — 그 하나를 처음부터 보여 준다.
  const mode = versioning ? 'file' : (canUpload ? (draft.mode ?? 'file') : 'link');
  const patchDraft = (patch) => setDraft((prior) => ({ ...prior, ...patch }));

  return (
    <div class="stack">
      {versioning ? (
        <p class="t-meta">이 자료는 보관 파일입니다. 새 판도 파일로 올립니다.</p>
      ) : (
        /* 「지금 켜진 것」을 가리키는 알약이다(v5 §12). 판단 축의 색을 쓰지 않는다 —
           무엇을 더할지 고르는 것은 판단이 아니다. */
        <div class="addres__modes" role="group" aria-label="자료를 더하는 방법">
          <button
            type="button"
            class="fpill"
            aria-pressed={mode === 'file'}
            disabled={!canUpload}
            onClick={() => patchDraft({ mode: 'file' })}
          >
            파일 올리기
          </button>
          <button
            type="button"
            class="fpill"
            aria-pressed={mode === 'link'}
            onClick={() => patchDraft({ mode: 'link' })}
          >
            URL · git 링크
          </button>
        </div>
      )}
      {canUpload ? null : <UploadUnavailableNotice capability={capability} />}

      {mode === 'file' && canUpload ? (
        <ResourceUploader
          spaceId={spaceId}
          assetId={assetId}
          limits={capability.limits}
          draft={draft}
          setDraft={setDraft}
          idPrefix="resource-upload"
          onUploaded={(manifest) => onDone?.(manifest)}
        />
      ) : mode === 'file' ? null : (
        <RegisterResourceForm
          spaceId={spaceId}
          draft={draft}
          setDraft={patchDraft}
          onDone={onDone}
        />
      )}
    </div>
  );
}

/**
 * 목록과 상세가 **한 화면**이다.
 *
 * 넓은 화면에서는 나란히 두고, 좁은 화면에서는 고른 것 하나만 보여 준다(그 폭에서는
 * 둘 다 보여 봐야 둘 다 못 읽는다). 전환은 CSS 가 하고, 「목록으로」는 검색·필터를
 * 그대로 들고 돌아온다 — 조건이 사라지면 사람은 처음부터 다시 찾아야 한다.
 */
function ResourceDirectory({
  assets, params, setParams, onQuery, spaceId, archived, referenceIndex, capability, onAdd, onNewVersion,
}) {
  const clearFilters = () => {
    onQuery('');
    setParams({ query: '', kind: 'all', source: 'all' }, { replace: true });
  };

  // ── 좁은 화면의 초점 (2026-09-12 검토) ──────────────────────────────────
  //
  // 그 폭에서는 고른 자료만 보이고 목록은 숨는다. 그런데 초점은 방금 누른 행에 그대로
  // 남는다 — 그 행은 이제 `display:none` 이라, 브라우저가 초점을 `<body>` 로 떨어뜨린다.
  // 그러면 화면이 바뀐 것을 스크린 리더는 듣지 못하고, 키보드는 문서 맨 위에서 다시 시작한다.
  //
  // 그래서 **보이는 쪽으로 초점을 옮기고**, 돌아올 때는 **떠났던 행으로 되돌린다.**
  const listRef = useRef(null);
  const detailRef = useRef(null);
  const restoreRow = useRef(null);
  const goBackToList = () => {
    restoreRow.current = params.assetId;
    setParams({ assetId: null, version: null });
  };

  useEffect(() => {
    if (typeof window === 'undefined') return;
    const narrow = typeof window.matchMedia === 'function'
      && window.matchMedia('(max-width: 959px)').matches;
    if (params.assetId) {
      // 넓은 화면에서는 목록이 옆에 그대로 있다 — 초점을 빼앗지 않는다.
      if (narrow) detailRef.current?.focus?.();
      return;
    }
    const target = restoreRow.current;
    restoreRow.current = null;
    if (!target || !narrow) return;
    const selector = `[data-asset-id="${typeof CSS !== 'undefined' && CSS.escape ? CSS.escape(target) : target}"]`;
    listRef.current?.querySelector(selector)?.focus?.();
  }, [params.assetId]);

  const facets = facetOptions(assets);
  const visible = filterAssets(assets, params);
  const active = filtersActive(params);
  const state = directoryState({ total: assets.length, visible: visible.length, filtersActive: active });
  const selected = findAsset(assets, params.assetId);
  const missingSelection = params.assetId !== null && selected === null;

  return (
    <div class={`resource-layout${selected ? ' resource-layout--split' : ''}`}>
      <div class="resource-layout__main">
        <section class="resource-directory" aria-label="이 Space의 자료">
          <header class="resource-directory__head">
            <span class="resource-directory__title">자료 목록</span>
            <span class="resource-directory__count">
              {active ? `${assets.length}개 중 ${visible.length}개` : `${assets.length}개`}
            </span>
          </header>

          {assets.length > 0 ? (
            <div class="resource-filters">
              <label class="visually-hidden" for="resourceSearch">자료 이름·출처로 찾기</label>
              <Input
                id="resourceSearch"
                size="sm"
                type="search"
                placeholder="이름·출처로 찾기"
                value={params.query}
                onInput={(event) => onQuery(event.currentTarget.value)}
              />
              {facets.kinds.length ? <>
                <label class="visually-hidden" for="resourceKindFilter">종류로 좁히기</label>
                <Select
                  id="resourceKindFilter"
                  size="sm"
                  value={params.kind}
                  onChange={(event) => setParams({ kind: event.currentTarget.value }, { replace: true })}
                >
                  {facets.kinds.map((option) => (
                    <option key={option.value} value={option.value}>
                      {option.value === 'all' ? '모든 종류' : option.label} ({option.count})
                    </option>
                  ))}
                </Select>
              </> : null}
              {facets.sources.length ? <>
                <label class="visually-hidden" for="resourceSourceFilter">주소 방식으로 좁히기</label>
                <Select
                  id="resourceSourceFilter"
                  size="sm"
                  value={params.source}
                  onChange={(event) => setParams({ source: event.currentTarget.value }, { replace: true })}
                >
                  {facets.sources.map((option) => (
                    <option key={option.value} value={option.value}>
                      {option.value === 'all' ? '모든 방식' : option.label} ({option.count})
                    </option>
                  ))}
                </Select>
              </> : null}
              {active ? (
                <Button size="sm" tone="ghost" onClick={clearFilters}>조건 지우기</Button>
              ) : null}
            </div>
          ) : null}

          {state === 'empty' ? (
            <div class="resource-empty">
              <span class="resource-empty__icon"><Icon name="resource" /></span>
              <div>
                <h2 class="t-section">아직 연결한 자료가 없습니다.</h2>
                <p class="t-meta">
                  {archived
                    ? '보관된 Space라 새 자료를 더할 수 없습니다. 남아 있는 자료도 없습니다.'
                    : '파일을 올리거나, 원문이 있는 위치를 연결해 함께 씁니다.'}
                </p>
              </div>
              {/* 보관된 Space 에서는 **권하지 않는다** — 눌러도 거절될 행위다. */}
              {archived ? null : <Button size="sm" tone="ghost" onClick={onAdd}>첫 자료 연결</Button>}
            </div>
          ) : state === 'no-results' ? (
            <div class="resource-empty">
              <span class="resource-empty__icon"><Icon name="resource" /></span>
              <div>
                <h2 class="t-section">조건에 맞는 자료가 없습니다.</h2>
                <p class="t-meta">이 Space에는 {assets.length}개가 있습니다. 검색어나 필터를 지우면 다시 보입니다.</p>
              </div>
              <Button size="sm" tone="ghost" onClick={clearFilters}>조건 지우기</Button>
            </div>
          ) : (
            <div class="resource-list" ref={listRef}>
              {visible.map((asset) => (
                <button
                  class={`resource-row${selected?.assetId === asset.assetId ? ' resource-row--selected' : ''}`}
                  type="button"
                  key={asset.assetId}
                  data-asset-id={asset.assetId}
                  aria-pressed={selected?.assetId === asset.assetId}
                  onClick={() => setParams({ assetId: asset.assetId, version: null })}
                >
                  <div class="resource-row__mark"><Icon name="resource" /></div>
                  <div class="resource-row__body">
                    <div class="resource-row__top"><h2>{asset.title}</h2><Chip>{kindLabel(asset.kind)}</Chip></div>
                    {/* 「검증된 위치」라고 말하지 않는다. 가진 사실만 적는다. */}
                    <p class="t-meta">{assetVersionLabel(asset)}</p>
                    <p class="t-meta">{assetSourceSummary(asset)}</p>
                  </div>
                </button>
              ))}
            </div>
          )}
        </section>
      </div>

      {missingSelection ? (
        <div class="resource-layout__detail" ref={detailRef} tabindex="-1" aria-label="찾지 못한 자료">
          <Notice tone="waiting" title="이 주소가 가리키는 자료를 목록에서 찾지 못했습니다.">
            <p class="t-meta">지워졌거나 다른 Space의 자료일 수 있습니다. 대신 다른 것을 골라 보여 주지 않습니다.</p>
          </Notice>
          <Button size="sm" onClick={goBackToList}>목록으로</Button>
        </div>
      ) : selected ? (
        <div class="resource-layout__detail" ref={detailRef} tabindex="-1" aria-label={`${selected.title} — 등록 정보`}>
          <ManifestDetail
            asset={selected}
            requestedVersion={params.version}
            onShowCurrent={() => setParams({ version: null })}
            referenceIndex={referenceIndex}
            spaceId={spaceId}
            archived={archived}
            capability={capability}
            onNewVersion={onNewVersion}
            onClose={goBackToList}
          />
        </div>
      ) : null}
    </div>
  );
}

export function ManifestDetail({
  asset, requestedVersion, onShowCurrent, referenceIndex, spaceId, archived, capability,
  onNewVersion, onClose,
}) {
  const session = useSession();
  // **주소가 판을 말했으면 그 판을 읽는다.** 목록이 아는 현재 판으로 바꿔 읽지 않는다 —
  // 넘김이 v1 로 고정해 둔 근거를 열었는데 v2 가 보이면, 받은 사람은 다른 것을 읽으면서
  // 같은 것을 읽었다고 믿는다.
  const version = shownVersion(asset, requestedVersion);
  const pinnedToOlder = isPinnedToOlderVersion(asset, requestedVersion);
  const manifest = useResource(
    `space:${spaceId}:resource:${asset.assetId}:${version ?? ''}`,
    () => read('resource', {
      nodeId: session.nodeId,
      query: { spaceId, assetId: asset.assetId, version },
    }),
  );

  return (
    <Panel
      // **이 자료의 이름이 제목이다.** 「선택한 자료의 등록 정보」는 어느 자료에나 맞는
      // 말이라 아무것도 알려 주지 않는다.
      title={asset.title}
      action={<span class="resource-detail__back"><Button size="sm" tone="ghost" onClick={onClose}>목록으로</Button></span>}
      /* `class` 는 Panel 의 것을 덮어쓴다(rest 가 뒤에 퍼진다). 카드 물성을 잃지 않도록
         `panel` 을 함께 적는다 — `panel--gate` 가 이미 쓰는 방식이다. */
      class="panel resource-manifest"
    >
      {pinnedToOlder ? (
        <Notice tone="neutral" title={`이 링크는 v${requestedVersion}을 가리킵니다.`}>
          <p class="t-meta">이 자료의 현재 판은 v{asset.currentVersion ?? asset.registeredVersion}입니다.</p>
          <div class="row"><Button size="sm" onClick={onShowCurrent}>현재 판 보기</Button></div>
        </Notice>
      ) : null}
      <Resource resource={manifest} loadingLabel="등록 정보를 읽는 중" compact>
        {(data) => {
          const shown = data ?? asset;
          const managed = sourceTypeOf(shown) === 'managed_file';
          return <>
            {/* 먼저 「이게 무엇인가」 — 파일 이름·형식·크기·판. 그다음 할 수 있는 일.
                세부(종류·주소 방식·checksum·보관 설명)는 접어 둔다. */}
            <AssetHeadline asset={shown} />
            {managed ? (
              <ManagedFileActions
                asset={shown}
                version={version}
                spaceId={spaceId}
                capability={capability}
                archived={archived}
                onNewVersion={onNewVersion}
              />
            ) : null}
            <ManifestDetails asset={shown} />
          </>;
        }}
      </Resource>
      <TasksUsingResource
        assetId={asset.assetId}
        referenceIndex={referenceIndex}
        spaceId={spaceId}
      />
    </Panel>
  );
}

/**
 * 보관 파일로 할 수 있는 일 — **인증된 내려받기 하나**와, png·jpeg 의 미리보기.
 *
 * 영구 공개 주소도 만료 URL 도 만들지 않는다. 누를 때마다 세션·Node·멤버십을 다시 지나는
 * 요청 하나가 나가고, 돌아온 바이트는 `blob:` 로 잠깐 살았다가 회수된다.
 *
 * **미리보기는 png·jpeg 뿐이다.** 서버가 그 둘에만 `disposition=inline` 을 허락한다
 * (다른 형식에 붙이면 400 으로 거절한다). PDF·HTML·SVG 를 앱 origin 에서 여는 길은
 * 여기에 없다 — 그건 첨부가 아니라 코드 실행이 될 수 있다.
 */
function ManagedFileActions({ asset, version, spaceId, capability, archived, onNewVersion }) {
  const session = useSession();
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const [preview, setPreview] = useState(false);
  const file = asset.file ?? null;
  const inlineTypes = capability?.limits?.inlinePreviewMediaTypes ?? [];
  const previewable = file?.mediaType ? inlineTypes.includes(file.mediaType) : false;

  async function download() {
    if (busy) return;
    setBusy(true);
    setError(null);
    let url = null;
    try {
      const content = await fetchResourceContent({
        spaceId,
        assetId: asset.assetId,
        version,
        nodeId: session.nodeId,
      });
      url = URL.createObjectURL(content.blob);
      const link = document.createElement('a');
      link.href = url;
      // 이름은 **서버가 정한 것**을 쓴다. 없으면 manifest 의 이름이고, 우리가 지어내지 않는다.
      link.download = content.filename ?? file?.filename ?? 'download';
      link.rel = 'noreferrer noopener';
      document.body.appendChild(link);
      link.click();
      link.remove();
    } catch (caught) {
      setError(describeError(caught).message);
    } finally {
      // 브라우저가 내려받기를 시작할 틈을 준 뒤 회수한다. 즉시 회수하면 그 내려받기가
      // 취소되는 브라우저가 있다.
      if (url) setTimeout(() => URL.revokeObjectURL(url), 1000);
      setBusy(false);
    }
  }

  return (
    <section class="managed-file" aria-label="보관 파일">
      <div class="managed-file__actions">
        <Button size="sm" busy={busy} onClick={download}>내려받기</Button>
        {previewable ? (
          <Button size="sm" tone="ghost" onClick={() => setPreview(!preview)}>
            {preview ? '미리보기 닫기' : '미리보기'}
          </Button>
        ) : null}
        {onNewVersion && !archived && capability?.state === 'ready' ? (
          <Button size="sm" tone="ghost" onClick={() => onNewVersion(asset)}>새 판 올리기</Button>
        ) : null}
      </div>
      {/* 접지 않는다. 내려받기 버튼 바로 옆에서 읽혀야 하는 두 문장이다. */}
      <p class="t-meta">
        내려받을 때마다 권한을 다시 확인합니다. 악성코드 검사는 하지 않습니다.
        {previewable ? '' : ' 이 형식은 이 화면에서 열지 않고 내려받아 여세요.'}
      </p>
      {preview && previewable ? (
        <ImagePreview spaceId={spaceId} assetId={asset.assetId} version={version} alt={file?.filename ?? '미리보기'} />
      ) : null}
      {error ? <Notice tone="danger" title={error} /> : null}
    </section>
  );
}

/** png·jpeg 한 장. 다 쓰면 `blob:` 주소를 **반드시** 회수한다. */
function ImagePreview({ spaceId, assetId, version, alt }) {
  const session = useSession();
  const [url, setUrl] = useState(null);
  const [failed, setFailed] = useState(null);

  useEffect(() => {
    const controller = new AbortController();
    let objectUrl = null;
    let cancelled = false;
    fetchResourceContent({
      spaceId, assetId, version, nodeId: session.nodeId,
      disposition: 'inline',
      signal: controller.signal,
    })
      .then((content) => {
        if (cancelled) return;
        objectUrl = URL.createObjectURL(content.blob);
        setUrl(objectUrl);
      })
      .catch((error) => {
        if (!cancelled) setFailed(describeError(error).message);
      });
    return () => {
      cancelled = true;
      controller.abort();
      if (objectUrl) URL.revokeObjectURL(objectUrl);
    };
  }, [spaceId, assetId, version, session.nodeId]);

  if (failed) return <p class="t-meta">{failed}</p>;
  if (!url) return <p class="t-meta">미리보기를 읽는 중입니다.</p>;
  return <img class="managed-file__preview" src={url} alt={alt} />;
}

function TasksUsingResource({ assetId, referenceIndex, spaceId }) {
  return <Resource resource={referenceIndex} loadingLabel="이 자료를 근거로 쓰는 Task를 읽는 중" compact>
    {(data) => {
      const tasks = uniqueTasksForAsset(data.references ?? [], assetId);
      if (tasks.length === 0) {
        return <section class="resource-task-uses" aria-label="이 자료를 근거로 쓰는 Task">
          <p class="t-meta">TASK REFERENCES</p>
          <h3 class="t-body-strong">아직 이 자료를 근거로 연결한 Task가 없습니다.</h3>
          <p class="t-meta">Task에서 자료 근거를 연결하면 제목과 상태만 이곳에 나타납니다.</p>
        </section>;
      }
      return <section class="resource-task-uses" aria-label="이 자료를 근거로 쓰는 Task">
        <p class="t-meta">TASK REFERENCES · {tasks.length}</p>
        <h3 class="t-body-strong">이 자료를 근거로 쓰는 Task</h3>
        <ul class="resource-task-uses__list">
          {tasks.map((task) => <li key={task.taskId}>
            <Link href={`/s/${spaceId}/task/${task.taskId}`} class="resource-task-use">
              <span>{task.title}</span><Chip>{task.state}</Chip>
            </Link>
          </li>)}
        </ul>
      </section>;
    }}
  </Resource>;
}

// snapshot/live가 여러 번 붙어도 한 Task는 한 번만 보인다. 이 화면이 설명하는 것은
// locator 세부가 아니라 "이 자료를 근거로 삼는 Task"다.
function uniqueTasksForAsset(references, assetId) {
  const seen = new Set();
  const tasks = [];
  for (const reference of references) {
    if (reference?.locator?.assetId !== assetId) continue;
    for (const task of reference.tasks ?? []) {
      if (seen.has(task.taskId)) continue;
      seen.add(task.taskId);
      tasks.push(task);
    }
  }
  return tasks;
}

/**
 * **먼저 무엇인가.** 파일이면 이름·형식·크기·판, 주소면 어디의 무엇인지·판.
 *
 * 이 한 줄이 제목 바로 아래 온다. 예전에는 이 자리에 종류·주소 방식·checksum 까지 열한 줄이
 * 한꺼번에 있어서, 정작 「이게 무슨 파일인지」가 목록에 묻혔다.
 */
function AssetHeadline({ asset }) {
  const file = sourceTypeOf(asset) === 'managed_file' ? (asset.file ?? null) : null;
  const version = asset.registeredVersion ?? asset.currentVersion ?? null;
  return (
    <div class="manifest-headline">
      <p class="t-body-strong manifest-headline__name">
        {file ? file.filename ?? '이름을 읽지 못한 파일' : asset.title}
      </p>
      <p class="t-meta">
        {file
          ? [mediaTypeLabel(file.mediaType), fileSizeLabel(file.byteSize) ?? '크기를 읽지 못함']
            .filter(Boolean).join(' · ')
          : assetSourceSummary(asset)}
        {version === null ? '' : ` · 등록 판 v${version}`}
      </p>
    </div>
  );
}

/**
 * 확인하려는 사람을 위한 자리 — **접어 둔다.**
 *
 * 여기 있는 것은 평소에 읽을 값이 아니다(종류·주소 방식·checksum·git 좌표). 그렇다고
 * 없애지는 않는다: 「이게 정말 그것인가」를 확인할 유일한 값들이다.
 */
function ManifestDetails({ asset }) {
  const type = sourceTypeOf(asset);
  const git = type === 'git' ? asset.source : null;
  const file = type === 'managed_file' ? (asset.file ?? null) : null;
  return (
    <details class="manifest-more">
      <summary class="t-meta">등록 정보</summary>
      <dl class="manifest-facts">
        <div><dt>자료 이름</dt><dd>{asset.title}</dd></div>
        <div><dt>종류</dt><dd>{kindLabel(asset.kind)}</dd></div>
        <div><dt>주소 방식</dt><dd>{sourceTypeLabel(type)}</dd></div>
        {/* 보관 파일이 **공개 manifest 에서 말하는 전부**다: 이름·형식·크기·checksum.
            객체 키도 저장 경로도 만료되는 주소도 어느 응답에도 없다. */}
        {file ? <>
          <div><dt>형식</dt><dd class="t-mono">{file.mediaType ?? '형식을 읽지 못함'}</dd></div>
          <div><dt>checksum</dt><dd class="t-mono manifest-facts__wrap">{file.checksumSha256 ?? '없음'}</dd></div>
        </> : (
          // 보관 파일에는 원본 시스템이 부르는 이름이 없다 — 그 칸을 「없음」으로 채우지 않는다.
          <div><dt>원문 표기</dt><dd>{asset.revision || '없음'}</dd></div>
        )}
        <div><dt>{git ? '저장소' : file ? '보관 위치' : '원문 위치'}</dt><dd>
          {asset.sourceRedacted
            ? '주소에 자격으로 보이는 값이 있어 일부를 가렸습니다. 원문 열기는 제공하지 않습니다.'
            : hasExternalOriginal(asset)
              ? <a href={asset.sourceUri} target="_blank" rel="noreferrer noopener">원문 열기</a>
              /* 보관 파일에는 밖으로 나가는 주소가 없다. **가짜 주소를 그리지 않는다** —
                 내려받기는 위의 인증된 요청 하나뿐이고, 그건 매 요청 권한을 다시 본다. */
              : file
                ? 'Loom이 보관합니다. 공개 주소는 없습니다.'
                : '이 판에는 밖으로 나가는 주소가 없습니다.'}
        </dd></div>
        {/* git 자료는 주소 하나로 다시 찾을 수 없다 — 판·파일·줄이 함께 있어야 같은 것을 가리킨다.
            그래서 그 셋을 감추지 않고 그대로 적는다. commit 은 줄이지 않는다: 이 화면은
            「무엇을 가리키는가」를 확인하는 자리이고, 줄인 값으로는 확인할 수 없다. */}
        {git ? <>
          <div><dt>commit</dt><dd class="t-mono manifest-facts__wrap">{git.commit}</dd></div>
          <div><dt>저장소 기준 경로</dt><dd class="t-mono">{git.path}</dd></div>
          <div><dt>줄 범위</dt><dd>{git.range ? `${git.range.startLine}–${git.range.endLine}` : '파일 전체'}</dd></div>
        </> : null}
      </dl>
      {/* 확인하지 않은 것을 확인했다고 적지 않는다. 이 줄이 옛 「검증된 위치」를 대신한다. */}
      {file ? null : (
        <p class="t-meta">Loom은 이 위치와 판만 기록합니다. 원문이 지금 열리는지, 내용이 그대로인지는 확인하지 않습니다.</p>
      )}
    </details>
  );
}

/**
 * 자료 등록 — **주소를 다시 찾는 방식**을 사람이 고른다.
 *
 * 두 방식의 차이는 편의가 아니라 사실이다: URL 하나로 다시 찾을 수 있는 자료가 있고,
 * 코드 저장소의 한 파일처럼 판·파일·줄이 함께 있어야 같은 것을 가리키는 자료가 있다.
 * 후자를 URL 안에 욱여넣으면(`#L10-L40`) 호스팅 서비스마다 다른 문법을 우리가 해석하게 된다.
 *
 * 적은 값은 **부모가 들고 있다.** 이 폼은 그 값을 그리기만 한다 — 대화상자를 닫아도,
 * 배경을 잘못 눌러도, 적던 것이 사라지지 않는다.
 */
function RegisterResourceForm({ spaceId, draft, setDraft, onDone }) {
  const session = useSession();
  const [localError, setLocalError] = useState(null);
  const register = useCommand(
    (params, idempotencyKey) => registerResource(params, { nodeId: session.nodeId, idempotencyKey }),
    { invalidates: [`space:${spaceId}:resources`] },
  );
  const dirty = draft.title !== '' || draft.sourceUri !== '' || draft.revision !== ''
    || draft.commit !== '' || draft.path !== '';

  return (
    <Form
      class="stack"
      busy={register.pending}
      onSubmit={async () => {
        const title = draft.title.trim();
        const sourceUri = draft.sourceUri.trim();
        if (!title || !sourceUri) {
          setLocalError('자료 이름과 원문 위치를 모두 입력하세요.');
          return;
        }
        try {
          // Parse only to catch a malformed locator in the client. The
          // server remains the authority on permitted schemes and scope.
          new URL(sourceUri);
        } catch {
          setLocalError('원문 위치는 완전한 URL이어야 합니다.');
          return;
        }
        let source = { type: 'uri' };
        if (draft.sourceType === 'git') {
          const commit = draft.commit.trim();
          const path = draft.path.trim();
          if (!commit || !path) {
            setLocalError('git 자료에는 commit과 저장소 기준 경로가 필요합니다.');
            return;
          }
          // 모양만 먼저 본다. 「이 값이 정말 불변인가」는 서버가 판정한다 —
          // 브랜치 이름이 판이 아니라는 것도 거기서 거절된다.
          if (!/^[0-9a-f]{40}$|^[0-9a-f]{64}$/.test(commit)) {
            setLocalError('commit은 전체 소문자 해시여야 합니다. 브랜치·태그는 판이 아닙니다.');
            return;
          }
          const startLine = Number.parseInt(draft.startLine, 10);
          const endLine = Number.parseInt(draft.endLine, 10);
          const range = Number.isInteger(startLine) && Number.isInteger(endLine)
            ? { startLine, endLine }
            : null;
          source = { type: 'git', commit, path, ...(range ? { range } : {}) };
        }
        setLocalError(null);
        const result = await register.run({
          spaceId,
          kind: draft.kind,
          title,
          sourceUri,
          revision: draft.revision.trim() || undefined,
          source,
        });
        if (result.ok) {
          onDone?.(result.data);
        }
      }}
    >
      <Field
        label="주소 방식"
        htmlFor="resourceSourceType"
        hint="git 저장소의 파일은 주소 하나로 다시 찾을 수 없습니다 — 판·파일·줄이 함께 필요합니다."
      >
        <Select
          id="resourceSourceType"
          name="sourceType"
          value={draft.sourceType}
          onChange={(event) => setDraft({ sourceType: event.currentTarget.value })}
        >
          <option value="uri">URL 하나</option>
          <option value="git">git 저장소의 파일</option>
        </Select>
      </Field>
      <Field label="종류" htmlFor="resourceKind">
        <Select
          id="resourceKind"
          name="kind"
          value={draft.kind}
          onChange={(event) => setDraft({ kind: event.currentTarget.value })}
        >
          {KINDS.map(([value, label]) => <option key={value} value={value}>{label}</option>)}
        </Select>
      </Field>
      <Field label="자료 이름" htmlFor="resourceTitle" required>
        <Input
          id="resourceTitle"
          name="title"
          placeholder="예: 2분기 이탈률 관찰 메모"
          value={draft.title}
          onInput={(event) => setDraft({ title: event.currentTarget.value })}
        />
      </Field>
      <Field
        label={draft.sourceType === 'git' ? '저장소 주소' : '원문 위치'}
        htmlFor="resourceUri"
        required
        hint="본문·파일·토큰을 붙이지 말고, 원문으로 돌아갈 주소를 남깁니다."
      >
        <Input
          id="resourceUri"
          name="sourceUri"
          type="url"
          inputMode="url"
          placeholder={draft.sourceType === 'git' ? '저장소 주소를 붙여 넣으세요' : '원문 URL을 붙여 넣으세요'}
          value={draft.sourceUri}
          onInput={(event) => setDraft({ sourceUri: event.currentTarget.value })}
        />
      </Field>
      {draft.sourceType === 'git' ? (
        <>
          <Field label="commit" htmlFor="resourceCommit" required hint="브랜치·태그는 판이 아닙니다. 전체 해시를 적습니다.">
            <Input id="resourceCommit" name="commit" value={draft.commit} onInput={(event) => setDraft({ commit: event.currentTarget.value })} />
          </Field>
          <Field label="저장소 기준 경로" htmlFor="resourcePath" required hint="저장소 밖을 가리키는 경로는 저장되지 않습니다.">
            <Input
              id="resourcePath"
              name="path"
              placeholder="예: task-ledger/src/domain/candidate-context.js"
              value={draft.path}
              onInput={(event) => setDraft({ path: event.currentTarget.value })}
            />
          </Field>
          <div class="resource-register__twocol">
            <Field label="시작 줄 (선택)" htmlFor="resourceStartLine">
              <Input id="resourceStartLine" name="startLine" inputMode="numeric" value={draft.startLine} onInput={(event) => setDraft({ startLine: event.currentTarget.value })} />
            </Field>
            <Field label="끝 줄 (선택)" htmlFor="resourceEndLine">
              <Input id="resourceEndLine" name="endLine" inputMode="numeric" value={draft.endLine} onInput={(event) => setDraft({ endLine: event.currentTarget.value })} />
            </Field>
          </div>
        </>
      ) : null}
      <Field label="원문 버전 (선택)" htmlFor="resourceRevision">
        <Input id="resourceRevision" name="revision" placeholder="예: 2026-08-13 또는 v4" value={draft.revision} onInput={(event) => setDraft({ revision: event.currentTarget.value })} />
      </Field>
      <p class="t-meta">이 방식은 주소만 남깁니다. 원문은 원래 있던 곳에 그대로 있습니다.</p>
      {localError ? <Notice tone="danger" title={localError} /> : null}
      {register.error ? <Notice tone="danger" title={describeError(register.error).message} /> : null}
      <div class="row">
        <Button kind="submit" tone="human" busy={register.pending}>자료 위치 연결</Button>
        {dirty ? (
          <Button size="md" tone="ghost" onClick={() => { setLocalError(null); setDraft(EMPTY_REGISTRATION); }}>
            입력 지우기
          </Button>
        ) : null}
      </div>
    </Form>
  );
}
